/**
 * Image mapping system using ES module imports for production compatibility
 * Maps JSON paths to imported asset URLs that work in both dev and production
 */

// Import all images as ES modules
import logo from "@/assets/images/logo.png";
import heroCockpit from "@/assets/images/hero-cockpit.jpg";

// Featured posts images
import celebrating35Years from "@/assets/images/featured-posts/celebrating-35-years.jpg";
import updatedWorkGallery from "@/assets/images/featured-posts/updated-work-gallery.jpg";
import newWebsite from "@/assets/images/featured-posts/new-website.jpg";
import warplaneMuseum from "@/assets/images/featured-posts/warplane-museum.jpg";

/**
 * Image mapping from JSON paths to imported asset URLs
 * This ensures proper asset handling in both development and production builds
 */
const imageMap: Record<string, string> = {
  // Logo mappings
  "/images/logo.png": logo,
  "/assets/images/logo.png": logo,
  "https://islipavionics.com/wp-content/uploads/2021/03/cropped-logo4.png": logo,
  "/wp-content/uploads/2021/03/cropped-logo4.png": logo,
  
  // Hero background mappings
  "/images/hero-cockpit.jpg": heroCockpit,
  "/assets/images/hero-cockpit.jpg": heroCockpit,
  "https://islipavionics.com/wp-content/uploads/2021/02/N2145X-panel-B-1920x1023.jpg": heroCockpit,
  "/wp-content/uploads/2021/02/N2145X-panel-B-1920x1023.jpg": heroCockpit,
  
  // Featured posts images
  "/images/featured-posts/celebrating-35-years.jpg": celebrating35Years,
  "/assets/images/featured-posts/celebrating-35-years.jpg": celebrating35Years,
  "https://islipavionics.com/wp-content/uploads/2021/12/Islip-Avionics-Emp_2021-02-280x190.jpg": celebrating35Years,
  "/wp-content/uploads/2021/12/Islip-Avionics-Emp_2021-02-280x190.jpg": celebrating35Years,
  
  "/images/featured-posts/updated-work-gallery.jpg": updatedWorkGallery,
  "/assets/images/featured-posts/updated-work-gallery.jpg": updatedWorkGallery,
  "https://islipavionics.com/wp-content/uploads/2021/10/N3174T-panel-after-1-280x190.jpg": updatedWorkGallery,
  "/wp-content/uploads/2021/10/N3174T-panel-after-1-280x190.jpg": updatedWorkGallery,
  
  "/images/featured-posts/new-website.jpg": newWebsite,
  "/assets/images/featured-posts/new-website.jpg": newWebsite,
  "https://islipavionics.com/wp-content/uploads/2021/11/N67L-Plane-280x190.jpg": newWebsite,
  "/wp-content/uploads/2021/11/N67L-Plane-280x190.jpg": newWebsite,
  
  "/images/featured-posts/warplane-museum.jpg": warplaneMuseum,
  "/assets/images/featured-posts/warplane-museum.jpg": warplaneMuseum,
  "https://islipavionics.com/wp-content/uploads/2017/10/C47_edit-780x400-280x190.jpg": warplaneMuseum,
  "/wp-content/uploads/2017/10/C47_edit-780x400-280x190.jpg": warplaneMuseum,
};

/**
 * Resolves image paths from JSON data to actual imported asset URLs
 * @param imagePath - Path from JSON data (e.g., "/assets/images/logo.png")
 * @returns Resolved asset URL that works in both dev and production
 */
export function resolveImagePath(imagePath: string): string {
  // First try exact match in image map
  if (imageMap[imagePath]) {
    return imageMap[imagePath];
  }

  // Try normalized paths (with and without /assets prefix)
  const normalizedPath = imagePath.startsWith("/assets/")
    ? imagePath
    : `/assets${imagePath.startsWith("/") ? imagePath : "/" + imagePath}`;

  if (imageMap[normalizedPath]) {
    return imageMap[normalizedPath];
  }

  // Try legacy path format
  const legacyPath = imagePath.replace("/assets", "");
  if (imageMap[legacyPath]) {
    return imageMap[legacyPath];
  }

  // Fallback: log warning and return original path
  console.warn(`Image not found in image map: ${imagePath}. Available images:`, Object.keys(imageMap));
  return imagePath;
}

/**
 * Get all available image paths for debugging
 */
export function getAvailableImages(): string[] {
  return Object.keys(imageMap);
}

/**
 * Legacy export for compatibility
 */
export const resolveImageUrl = resolveImagePath;
